import {
  Component,
  Output,
  EventEmitter,
  Input,
  Inject,
  OnInit,
  PLATFORM_ID,
  NgZone,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ApiServiceService } from 'src/app/services/api-service.service';

declare var Razorpay: any;

@Component({
  selector: 'app-payment-option',
  templateUrl: './payment-option.component.html',
  styleUrls: ['./payment-option.component.scss'],
})
export class PaymentOptionComponent implements OnInit {
  @Input() isProcessing = false;
  @Input() totalAmount = 0;
  @Input() mrpAmount = 0;
  @Input() couponDiscount = 0;
  // optional prefill
  @Input() customerName = '';
  @Input() customerEmail = '';
  @Input() customerContact = '';

  @Output() paymentSuccess = new EventEmitter<any>();
  @Output() paymentFailed = new EventEmitter<any>();
  @Output() codSelected = new EventEmitter<void>();
  @Output() selectPaymentOption = new EventEmitter<string>(); // 'online' | 'cod'
  @Output() goBack = new EventEmitter<void>();

  selectedPaymentCard: 'online' | 'cod' = 'online';
  feesExpanded = true;
  discountsExpanded = true;

  loading = false;
  paymentMessage = '';

  readonly PLATFORM_FEE = 5;
  readonly COD_HANDLING_FEE = 30;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private api: ApiServiceService,
    private zone: NgZone,
  ) {}

  ngOnInit() {
    this.selectPaymentCard('online');
  }

  private isBrowser() {
    return isPlatformBrowser(this.platformId);
  }

  private loadRazorpayScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.isBrowser()) return reject('NOT_BROWSER');
      if ((window as any).Razorpay) return resolve();

      document.getElementById('razorpay-sdk')?.remove();
      const script = document.createElement('script');
      script.id = 'razorpay-sdk';
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject('SDK_LOAD_FAILED');
      document.body.appendChild(script);
    });
  }

  selectPaymentCard(card: 'online' | 'cod') {
    this.selectedPaymentCard = card;
    this.paymentMessage = '';
    this.selectPaymentOption.emit(card);
  }

  /** "Pay" button */
  async startOnlinePayment() {
    if (this.loading || this.isProcessing) return;
    this.paymentMessage = '';
    this.loading = true;

    const amountInPaise = Math.round(this.onlineTotal * 100);
    if (amountInPaise < 100) {
      this.loading = false;
      this.paymentMessage = 'Amount is too low to process online.';
      return;
    }

    try {
      await this.loadRazorpayScript();
    } catch {
      this.loading = false;
      this.paymentMessage =
        'Could not load the payment SDK. Check your connection.';
      return;
    }

    this.api.createRazorpayOrder(amountInPaise).subscribe({
      next: (order: any) => {
        if (!order?.order_id) {
          this.loading = false;
          this.paymentMessage = 'Could not create the order. Please try again.';
          return;
        }
        this.openRazorpay(order);
      },
      error: (err) => {
        this.loading = false;
        this.paymentMessage =
          err?.error?.message || 'Could not create the order.';
        this.paymentFailed.emit(err);
      },
    });
  }

  private openRazorpay(order: any) {
    const options = {
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.order_id,
      name: 'Your Store Name', // change
      description: 'Order payment',
      prefill: {
        name: this.customerName,
        email: this.customerEmail,
        contact: this.customerContact,
      },
      theme: { color: '#7B68EE' },

      // Payment completed in the modal -> verify on the server
      handler: (response: any) => {
        this.zone.run(() => this.verifyPayment(response));
      },

      // User closed the modal without paying
      modal: {
        ondismiss: () => {
          this.zone.run(() => {
            this.loading = false;
            this.paymentMessage = 'Payment cancelled.';
          });
        },
      },
    };

    const rzp = new Razorpay(options);

    rzp.on('payment.failed', (resp: any) => {
      this.zone.run(() => {
        this.loading = false;
        this.paymentMessage =
          resp?.error?.description || 'Payment failed. Please try again.';
        this.paymentFailed.emit(resp?.error);
      });
    });

    rzp.open();
  }

  private verifyPayment(response: any) {
    this.api
      .verifyRazorpayPayment({
        razorpay_order_id: response.razorpay_order_id,
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_signature: response.razorpay_signature,
      })
      .subscribe({
        next: (res: any) => {
          this.loading = false;
          if (res?.success) {
            this.paymentSuccess.emit({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
            });
          } else {
            this.paymentMessage = 'Payment could not be verified.';
            this.paymentFailed.emit('VERIFY_FAILED');
          }
        },
        error: (err) => {
          this.loading = false;
          this.paymentMessage =
            err?.error?.message ||
            'Payment verification failed. If money was deducted, contact support.';
          this.paymentFailed.emit('VERIFY_FAILED');
        },
      });
  }

  /** COD button */
  placeCodOrder() {
    if (this.isProcessing) return;
    this.codSelected.emit();
  }

  toggleFees() {
    this.feesExpanded = !this.feesExpanded;
  }
  toggleDiscounts() {
    this.discountsExpanded = !this.discountsExpanded;
  }

  get paymentHandlingFee(): number {
    return this.selectedPaymentCard === 'cod' ? this.COD_HANDLING_FEE : 0;
  }
  get mrpDiscount(): number {
    return Math.max(this.mrpAmount - this.totalAmount, 0);
  }
  get totalDiscounts(): number {
    return this.mrpDiscount + this.couponDiscount;
  }
  get onlineTotal(): number {
    return Math.max(
      this.mrpAmount + this.PLATFORM_FEE - this.totalDiscounts,
      0,
    );
  }
  get codTotal(): number {
    return Math.max(
      this.mrpAmount +
        this.PLATFORM_FEE +
        this.COD_HANDLING_FEE -
        this.totalDiscounts,
      0,
    );
  }
  get grandTotal(): number {
    return this.selectedPaymentCard === 'cod'
      ? this.codTotal
      : this.onlineTotal;
  }
}
